import React from 'react';
import { Link } from 'react-router-dom';
import { Player, PlayerWithLeagueContext, MetadataFieldDefinition } from '../generated-api';
import PlayerAvatar from './common/PlayerAvatar';
import CompactRatingDisplay from './common/CompactRatingDisplay';

interface TeamResult {
  teamName: string;
  playerIds: string[];
  position: number;
}

interface LeagueTeamResultsProps {
  leagueId: string;
  teams: TeamResult[];
  players: (Player | PlayerWithLeagueContext)[];
  ratingChanges: Record<string, number>;
  ratingsBefore: Record<string, { rating: number; sigma: number }>;
  ratingsAfter: Record<string, { rating: number; sigma: number }>;
  winProbabilities: Record<string, number>;
  getPlayerName: (playerId: string) => string;
  teamMetadata?: Record<string, Record<string, any>>; // Team name -> field name -> value
  metadataFields?: MetadataFieldDefinition[]; // Available metadata fields
  currentRatings?: Record<string, { rating: number; sigma: number; gamesPlayed: number }>; // Current ratings for live plays
}

const LeagueTeamResults: React.FC<LeagueTeamResultsProps> = ({
  leagueId,
  teams,
  players,
  ratingChanges,
  ratingsBefore,
  ratingsAfter,
  winProbabilities,
  getPlayerName,
  teamMetadata,
  metadataFields = [],
  currentRatings
}) => {
  // Calculate team effective rating
  const calculateTeamEffectiveRating = (playerIds: string[]): {
    effectiveRating: number;
    baseRating: number;
    bonusPercent: number;
  } => {
    const playerRatings = playerIds
      .map(playerId => ratingsBefore[playerId]?.rating)
      .filter(rating => rating !== undefined);

    if (playerRatings.length === 0) return { effectiveRating: 0, baseRating: 0, bonusPercent: 0 };

    const maxRating = Math.max(...playerRatings);
    const teamSize = playerRatings.length;
    const bonusPercent = (teamSize - 1) * 10;
    const bonusMultiplier = 1 + (0.1 * (teamSize - 1));
    const effectiveRating = Math.round(maxRating * bonusMultiplier * 100) / 100;

    return {
      effectiveRating,
      baseRating: Math.round(maxRating * 100) / 100,
      bonusPercent
    };
  };

  return (
    <div className="w-full space-y-2">
      {teams.map(({ teamName, playerIds, position }) => {
        const isSinglePlayer = playerIds.length === 1;
        const { effectiveRating, baseRating } = calculateTeamEffectiveRating(playerIds);

        if (isSinglePlayer) {
          // Single-player team: Use compact unified card
          const playerId = playerIds[0];
          const player = players.find(p => p.id === playerId);
          const ratingChange = ratingChanges[playerId];
          const ratingBefore = ratingsBefore[playerId];
          const ratingAfter = ratingsAfter[playerId];
          const teamData = teamMetadata?.[teamName];
          const currentRatingForPlayer = currentRatings?.[playerId];

          return (
            <CompactPlayerResultCard
              key={`${leagueId}-${teamName}`}
              player={player}
              playerId={playerId}
              position={position}
              ratingChange={ratingChange}
              ratingBefore={ratingBefore}
              ratingAfter={ratingAfter}
              winProbability={winProbabilities[teamName]}
              getPlayerName={getPlayerName}
              teamMetadata={teamData}
              metadataFields={metadataFields}
              currentRating={currentRatingForPlayer}
            />
          );
        } else {
          // Multi-player team: Keep team structure with compact header
          const teamData = teamMetadata?.[teamName];
          const metadataEntries = teamData 
            ? Object.entries(teamData).filter(([_, value]) => value !== undefined && value !== null && value !== '')
            : [];

          return (
            <div key={`${leagueId}-${teamName}`} className="w-full border border-edge rounded-lg p-3" data-testid="team-result-card">
              {/* Compact single-line team header */}
              <div className="flex items-center justify-between mb-2 pb-2 border-b border-edge-subtle">
                <div className="flex items-center space-x-2 flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-heading truncate">{teamName}</h3>
                  <span className={`text-xs font-medium flex-shrink-0 px-1.5 py-0.5 rounded-full ${
                    position === 1
                      ? 'bg-amber-100 text-amber-900'
                      : position === 2
                      ? 'bg-stone-200 text-stone-700'
                      : position === 3
                      ? 'bg-orange-100 text-orange-800'
                      : 'bg-stone-100 text-stone-600'
                  }`}>#{position}</span>
                  {effectiveRating > 0 && playerIds.length > 1 && (
                    <span className="text-xs text-indigo-600 font-medium flex-shrink-0" title={`Team Rating: Max player rating (${baseRating}) × (1 + 0.1 × (team size - 1)) = ${baseRating} × ${(1 + (0.1 * (playerIds.length - 1))).toFixed(2)} = ${effectiveRating}`}>
                      {effectiveRating}
                    </span>
                  )}
                </div>
                <div className="flex-shrink-0 ml-2 text-right">
                  <div className="text-xs text-muted">Win Prob</div>
                  <div className="text-sm font-bold text-primary-700">
                    {winProbabilities[teamName] !== undefined ? `${winProbabilities[teamName]}%` : 'N/A'}
                  </div>
                </div>
              </div>

              {/* Metadata chips under Win Probability */}
              {metadataEntries.length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {metadataEntries.map(([fieldName, value]) => {
                    const field = metadataFields.find(f => f.name === fieldName);
                    const displayName = field?.displayName || fieldName;
                    const fieldType = field?.type || 'text';
                    
                    const displayValue = fieldType === 'boolean' 
                      ? (value ? 'Yes' : 'No')
                      : String(value);
                    
                    return (
                      <span
                        key={fieldName}
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-subtle text-heading"
                      >
                        {displayName}: {displayValue}
                      </span>
                    );
                  })}
                </div>
              )}

              {/* Player list with reduced spacing */}
              <div className="space-y-2">
                {playerIds.map(playerId => {
                  const player = players.find(p => p.id === playerId);
                  const ratingChange = ratingChanges[playerId];
                  const ratingBefore = ratingsBefore[playerId];
                  const ratingAfter = ratingsAfter[playerId];

                  const currentRating = currentRatings?.[playerId];
                  return (
                    <div key={`${leagueId}-${playerId}`}>
                      <PlayerLeagueRatingCard
                        player={player}
                        playerId={playerId}
                        position={position}
                        ratingChange={ratingChange}
                        ratingBefore={ratingBefore}
                        ratingAfter={ratingAfter}
                        getPlayerName={getPlayerName}
                        currentRating={currentRating}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
      })}
    </div>
  );
};

interface CompactPlayerResultCardProps {
  player: Player | PlayerWithLeagueContext | undefined;
  playerId: string;
  position: number;
  ratingChange?: number;
  ratingBefore?: { rating: number; sigma: number };
  ratingAfter?: { rating: number; sigma: number };
  winProbability?: number;
  getPlayerName: (playerId: string) => string;
  teamMetadata?: Record<string, any>;
  metadataFields?: MetadataFieldDefinition[];
  currentRating?: { rating: number; sigma: number; gamesPlayed: number };
}

const CompactPlayerResultCard: React.FC<CompactPlayerResultCardProps> = ({
  player,
  playerId,
  position,
  ratingChange,
  ratingBefore,
  ratingAfter,
  winProbability,
  getPlayerName,
  teamMetadata,
  metadataFields = [],
  currentRating
}) => {
  // Get all metadata entries
  const metadataEntries = teamMetadata 
    ? Object.entries(teamMetadata).filter(([_, value]) => value !== undefined && value !== null && value !== '')
    : [];

  return (
    <div className="w-full border border-edge rounded-lg p-3 bg-surface hover:bg-surface-hover transition-all active:scale-[0.98]" data-testid="team-result-card">
      {/* First row: Avatar + Name + Win Probability */}
      <Link
        data-track="result_player_link"
        to={`/players/${playerId}`}
        className="flex w-full min-h-[44px] items-center justify-between"
      >
        <div className="flex items-center space-x-2 flex-1 min-w-0">
          <div className="flex-shrink-0">
            <PlayerAvatar 
              name={player?.name || getPlayerName(playerId)} 
              imageUrl={player?.imageUrl} 
              size="small" 
            />
          </div>
          <div className="flex items-center space-x-2">
            <span className="font-medium text-heading truncate">
              {getPlayerName(playerId)}
            </span>
            <span className={`flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full ${
              position === 1
                ? 'bg-amber-100 text-amber-900'
                : position === 2
                ? 'bg-stone-200 text-stone-700'
                : position === 3
                ? 'bg-orange-100 text-orange-800'
                : 'bg-stone-100 text-stone-600'
            }`}>
              #{position}
            </span>
          </div>
        </div>

        {/* Right: Win Probability */}
        {winProbability !== undefined && (
          <div className="flex items-center space-x-3 flex-shrink-0 ml-3">
            <div className="text-right">
              <div className="text-xs text-muted">Win Prob</div>
              <div className="text-sm font-semibold text-primary-700">
                {winProbability}%
              </div>
            </div>
          </div>
        )}
      </Link>

      {/* Metadata chips under Win Probability */}
      {metadataEntries.length > 0 && (
        <div className="mt-1.5 ml-12 flex flex-wrap gap-1.5">
          {metadataEntries.map(([fieldName, value]) => {
            const field = metadataFields.find(f => f.name === fieldName);
            const displayName = field?.displayName || fieldName;
            const fieldType = field?.type || 'text';
            const displayValue = fieldType === 'boolean' 
              ? (value ? 'Yes' : 'No')
              : String(value);
            
            return (
              <span
                key={fieldName}
                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-subtle text-heading"
              >
                {displayName}: {displayValue}
              </span>
            );
          })}
        </div>
      )}

      {/* Second row: Rating display (only when before/after ratings exist) */}
      {ratingBefore !== undefined && ratingAfter !== undefined && (
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-2 ml-0 sm:ml-12">
          <CompactRatingDisplay
            rating={ratingBefore.rating}
            sigma={ratingBefore.sigma}
            className="text-[11px] sm:text-xs"
          />
          <div className="flex flex-col items-center">
            {ratingChange !== undefined && (
              <span className={`text-xs font-semibold ${ratingChange > 0 ? 'text-green-700' : ratingChange < 0 ? 'text-red-600' : 'text-body'}`}>
                {ratingChange > 0 ? '▲ +' : ratingChange < 0 ? '▼ ' : ''}{ratingChange.toFixed(1)}
              </span>
            )}
            <span className="text-disabled">→</span>
          </div>
          <CompactRatingDisplay
            rating={ratingAfter.rating}
            sigma={ratingAfter.sigma}
            className="text-[11px] sm:text-xs"
          />
        </div>
      )}

      {/* Rating (only for currentRating or fallback) */}
      {(currentRating || (ratingBefore === undefined && ratingAfter === undefined)) && (
        <Link
          data-track="result_player_link"
          to={`/players/${playerId}`}
          className="flex items-center space-x-3 mt-0.5 ml-12"
        >
          {currentRating ? (
            <CompactRatingDisplay
              rating={currentRating.rating}
              sigma={currentRating.sigma}
              isUncertain={(currentRating.gamesPlayed < 5) || (currentRating.sigma > 5)}
              className="text-xs"
            />
          ) : (
            <span className="text-xs text-muted">
              Rating: {player ? (('leagueRating' in player && player.leagueRating) ? Math.round(player.leagueRating.rating) : '25') : 'N/A'}
            </span>
          )}
        </Link>
      )}
    </div>
  );
};

interface PlayerLeagueRatingCardProps {
  player: Player | PlayerWithLeagueContext | undefined;
  playerId: string;
  position: number;
  ratingChange?: number;
  ratingBefore?: { rating: number; sigma: number };
  ratingAfter?: { rating: number; sigma: number };
  getPlayerName: (playerId: string) => string;
  currentRating?: { rating: number; sigma: number; gamesPlayed: number };
}

const PlayerLeagueRatingCard: React.FC<PlayerLeagueRatingCardProps> = ({
  player,
  playerId,
  position,
  ratingChange,
  ratingBefore,
  ratingAfter,
  getPlayerName,
  currentRating
}) => {
  return (
    <Link
      data-track="result_player_link"
      to={`/players/${playerId}`}
      className="flex w-full min-h-[44px] items-center justify-between border border-edge rounded-lg p-3 bg-surface hover:bg-surface-hover transition-all active:scale-[0.98] cursor-pointer"
    >
      {/* Left: Avatar + Name + Position + Rating */}
      <div className="flex items-center space-x-2 flex-1 min-w-0">
        <div className="flex-shrink-0">
          <PlayerAvatar name={player?.name || getPlayerName(playerId)} imageUrl={player?.imageUrl} size="small" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Name */}
          <div className="font-medium text-heading truncate">
            {getPlayerName(playerId)}
          </div>
          {/* Rating */}
          <div className="mt-0.5">
            {ratingBefore !== undefined && ratingAfter !== undefined ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <CompactRatingDisplay
                  rating={ratingBefore.rating}
                  sigma={ratingBefore.sigma}
                  className="text-xs"
                />
                <div className="flex flex-col items-center">
                  {ratingChange !== undefined && (
                    <span className={`text-xs font-semibold ${ratingChange > 0 ? 'text-green-700' : ratingChange < 0 ? 'text-red-600' : 'text-body'}`}>
                      {ratingChange > 0 ? '▲ +' : ratingChange < 0 ? '▼ ' : ''}{ratingChange.toFixed(1)}
                    </span>
                  )}
                  <span className="text-disabled">→</span>
                </div>
                <CompactRatingDisplay
                  rating={ratingAfter.rating}
                  sigma={ratingAfter.sigma}
                  className="text-xs"
                />
              </div>
            ) : currentRating ? (
              <CompactRatingDisplay
                rating={currentRating.rating}
                sigma={currentRating.sigma}
                isUncertain={(currentRating.gamesPlayed < 5) || (currentRating.sigma > 5)}
                className="text-xs"
              />
            ) : (
              <span className="text-xs text-muted">
                Rating: {player ? (('leagueRating' in player && player.leagueRating) ? Math.round(player.leagueRating.rating) : '25') : 'N/A'}
              </span>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default LeagueTeamResults;
